import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";

interface LazyMapEmbedProps {
  /** The map embed URL (e.g. a Google Maps `output=embed` iframe src). */
  src: string;
  /** Accessible title for the map iframe and load affordance. */
  title: string;
  className?: string;
}

/**
 * Defers loading a heavy third-party map embed (Google Maps pulls ~395 KiB of
 * JS) until the map is about to enter the viewport, or the user clicks the
 * placeholder. This keeps that payload off the initial page load entirely —
 * the map section sits far below the fold, so most first paints never touch it.
 */
export default function LazyMapEmbed({ src, title, className }: LazyMapEmbedProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);

  return (
    <div ref={ref} className={className}>
      {show ? (
        <iframe
          title={title}
          src={src}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <button
          type="button"
          onClick={() => setShow(true)}
          aria-label={`Load ${title}`}
          className="w-full h-full flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
        >
          <MapPin className="w-8 h-8" aria-hidden="true" />
          <span className="text-sm font-medium">View interactive map</span>
        </button>
      )}
    </div>
  );
}
